<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPlay, faPause, faClose, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import { useAudioPlayer } from '@/composables/useAudioPlayer'

const audio = useAudioPlayer()
const route = useRoute()

// close audio player on route change
watch(() => route.path, (newPath) => {
  if (!!audio.src.value && newPath)
    audio.close()
})

// Some routes have a pagination / navigation bar at the bottom.
// Adding them here will ensure the audio player is positioned above it.
const routeParamsWithBottomNav = ['longtextSlug'] // Add route params as needed
const positionFromBottom = computed(() => {
  if (routeParamsWithBottomNav.some(param => param in route.params))
    return '72px'
  else return '24px'
})

const playPauseIcon = computed(() => {
  if (audio.isLoading.value) return faSpinnerThird
  return audio.controls.playing.value ? faPause : faPlay
})

// used to color the timeline based on the percentage played
const percentagePlayed = computed(() => audio.formatted.value.percentagePlayed)

const handleAudioRangeInput = ({ target }: Event) => {
  const { value } = target as HTMLInputElement
  audio.controls.currentTime.value = Number(value)
}
</script>

<template>
  <Transition>
    <div
      v-show="audio.src.value"
      lang="no"
      class="audio-player"
      aria-label="Lydavspiller"
      role="region"
      tabindex="0"
    >
      <audio
        :ref="audio.playerEl"
        :src="audio.src.value"
        preload="metadata"
        autoplay
      />

      <div class="controls">
        <CommonButton
          decoration="fill"
          shape="icon"
          aria-label="Spill av lyd"
          :is-disabled="audio.isLoading.value"
          :aria-pressed="audio.controls.playing.value"
          :data-playing="audio.controls.playing.value"
          :class="{ rotate: audio.isLoading.value }"
          @click="audio.playPause"
        >
          <FontAwesomeIcon :icon="playPauseIcon" />
        </CommonButton>

        <label class="timeline">
          <span class="sr-only">Tidslinje</span>
          <span aria-hidden="true">{{ audio.formatted.value.currentTime }}</span>
          <input
            type="range"
            step="any"
            min="0"
            :max="audio.controls.duration.value"
            :value="audio.controls.currentTime.value"
            :aria-valuetext="`${audio.formatted.value.flooredPercentagePlayed} avspilt`"
            @input="handleAudioRangeInput"
            @keydown.space.prevent="audio.playPause"
          >
          <span aria-hidden="true">{{ audio.formatted.value.duration }}</span>
        </label>

        <CommonButton
          shape="icon"
          decoration="fill"
          aria-label="Lukk lydavspiller"
          @click="audio.close"
        >
          <FontAwesomeIcon :icon="faClose" />
        </CommonButton>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
    @include color-pairs;

    .audio-player {
        position: fixed;
        bottom: v-bind(positionFromBottom);
        padding: 0 8px;
        width: 100%;
        z-index: 1000;
        display: flex;
        justify-content: center;
    }

    .controls {
        display: flex;
        align-items: center;
        gap: 12px;
        max-width: 800px;
        width: 100vw;
        background: var(--color-blue-60);
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 0 6px rgb(0 0 0 / 40%);
    }

    .timeline {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    @media (width <= 600px) {
      .controls, .timeline {
        gap: 4px;
        padding: 8px;
        font-size: 0.8rem;
      }
    }

    .vgs-button, label {
        color: var(--color-yellow-10);
        flex-shrink: 0;
    }

    .vgs-button.fill, .vgs-button.fill:disabled {
      background: var(--color-blue-60);
      opacity: 1;
      border: none;
    }

    .vgs-button.fill:hover:not(:disabled),
    .vgs-button.fill:focus-visible:not(:disabled) {
      color: var(--color-yellow-30);
    }

    .controls input[type="range"] {
        flex-grow: 1;
        height: 6px;
        border-radius: 3px;
        box-shadow: 0 0 6px rgb(0 0 0 / 40%);
        background:linear-gradient(to right, var(--color-yellow-10) v-bind(percentagePlayed), rgb(255 255 255 / 25%) v-bind(percentagePlayed));
    }

    .controls input[type="range"]:focus-visible {
        @include focus-ring;
    }

    .controls input[type="range"]::-webkit-slider-thumb,
    .controls input[type="range"]::-moz-range-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 1px solid var(--color-lightblue-20);
        background: var(--color-yellow-10);
        cursor: pointer;
        box-shadow: 0 0 6px rgb(0 0 0 / 40%);
    }

    .rotate {
        animation: rotate .9s linear infinite;
    }

    .v-enter-active,
    .v-leave-active {
        transform: translateY(0%);
    }

    .v-enter-active {
        transition: transform 0.4s cubic-bezier(.25,1.09,.57,1.09);
    }

    .v-leave-active {
        transition: transform 0.4s cubic-bezier(.65,-0.33,.72,.51);
    }

    .v-enter-from,
    .v-leave-to {
        transform: translateY(300%);
    }
</style>
